import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { procApi } from '../../api/procurement.js';

export default function GoodsReceipts() {
  const [params] = useSearchParams();
  if (params.get('po')) return <NewGR poId={params.get('po')} />;
  return <GRList />;
}

function GRList() {
  const [items, setItems] = useState([]);
  useEffect(() => { procApi.listGRs().then((d) => setItems(d?.items || [])); }, []);
  return (
    <section>
      <header className="page-header"><h1>Goods Receipts</h1></header>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>Receipt #</th><th>PO</th><th>Warehouse</th><th>Date</th><th>Received By</th></tr></thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id}>
                <td><code>{g.receipt_no}</code></td>
                <td><Link to={`/procurement/orders/`}><code>{g.po_no}</code></Link></td>
                <td>{g.warehouse_name}</td>
                <td>{g.received_date}</td>
                <td className="muted">{g.received_by_name || '—'}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No receipts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NewGR({ poId }) {
  const nav = useNavigate();
  const [po, setPo] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    procApi.getPO(poId).then((d) => {
      setPo(d?.po);
      setWarehouseId(d?.po?.warehouse_id || '');
      const lines = typeof d?.po?.items === 'string' ? JSON.parse(d.po.items) : d?.po?.items || [];
      setItems(lines.map((l, idx) => ({
        poLineIdx: idx,
        productId: l.productId || null,
        description: l.description,
        ordered: Number(l.quantity || 0),
        alreadyReceived: Number(l.qtyReceived || 0),
        qtyReceived: Math.max(0, Number(l.quantity || 0) - Number(l.qtyReceived || 0)),
        unitCost: Number(l.unitPrice || 0),
        batchNo: '',
        expiryDate: '',
      })));
    });
    procApi.warehouses().then((d) => setWarehouses(d?.items || []));
  }, [poId]);

  function setLine(i, k, v) { setItems((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l))); }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        poId,
        warehouseId,
        receivedDate,
        notes: notes || null,
        items: items.filter((i) => Number(i.qtyReceived) > 0).map((i) => ({
          poLineIdx: i.poLineIdx,
          productId: i.productId || null,
          qtyReceived: Number(i.qtyReceived),
          unitCost: Number(i.unitCost),
          batchNo: i.batchNo || null,
          expiryDate: i.expiryDate || null,
        })),
      };
      if (!payload.items.length) throw new Error('Nothing to receive');
      await procApi.createGR(payload);
      nav(`/procurement/orders/${poId}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed');
    }
  }

  if (!po) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <h1>Goods Receipt</h1>
        <p className="muted">PO {po.po_no} · {po.vendor_name}</p>
      </header>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={submit} className="card form-card">
        <div className="grid-form">
          <label>Warehouse
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="">Select…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label>Received Date<input type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} required /></label>
          <label className="full">Notes<textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        </div>

        <table className="data-table je-grid">
          <thead><tr><th>Line</th><th>Ordered</th><th>Already</th><th>Receive Qty</th><th>Unit Cost</th><th>Batch No</th><th>Expiry</th></tr></thead>
          <tbody>
            {items.map((l, i) => (
              <tr key={i}>
                <td><strong>{l.description}</strong></td>
                <td>{l.ordered}</td>
                <td>{l.alreadyReceived}</td>
                <td><input type="number" step="0.01" value={l.qtyReceived} onChange={(e) => setLine(i, 'qtyReceived', e.target.value)} /></td>
                <td><input type="number" step="0.01" value={l.unitCost} onChange={(e) => setLine(i, 'unitCost', e.target.value)} /></td>
                <td><input value={l.batchNo} onChange={(e) => setLine(i, 'batchNo', e.target.value)} placeholder="optional" /></td>
                <td><input type="date" value={l.expiryDate} onChange={(e) => setLine(i, 'expiryDate', e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="actions">
          <button type="submit" className="btn primary">Confirm Receipt</button>
        </div>
      </form>
    </section>
  );
}
